import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Form, Accordion } from 'react-bootstrap';
import { getAccountingDashboard } from '../services/api';
import { toast } from 'react-toastify';
import { useMediaQuery } from 'react-responsive';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import it from 'date-fns/locale/it'; // Importa la locale italiana
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const ContabilitaDashboard = () => {
    const isMobile = useMediaQuery({ maxWidth: 991 });
    const [dashboardData, setDashboardData] = useState(null);
    const [assetAccounts, setAssetAccounts] = useState([]);
    const [assetChartData, setAssetChartData] = useState(null); // Renamed from chartData for clarity
    const [expenseRevenueChartData, setExpenseRevenueChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [startDate, setStartDate] = useState(startOfMonth(new Date()));
    const [endDate, setEndDate] = useState(endOfMonth(new Date()));

    const processAssetChartData = (accounts) => {
        const groups = {
            modenacinema: { prefix: "MC - ", data: [], labels: [] },
            giorgio: { prefix: "G - ", data: [], labels: [] },
            francesca: { prefix: "F - ", data: [], labels: [] },
            famiglia: { prefix: "FAM - ", data: [], labels: [] },
        };

        accounts.forEach(account => {
            for (const key in groups) {
                if (account.name.startsWith(groups[key].prefix)) {
                    // Consider all non-zero balances. User only asked for positive, but a full pie chart should reflect total.
                    if (account.balance !== 0) { 
                        groups[key].data.push(account.balance);
                        groups[key].labels.push(`${account.name.replace(groups[key].prefix, '')}`);
                    }
                    return; // Move to next account once matched
                }
            }
        });

        const finalChartData = {};
        for (const key in groups) {
            if (groups[key].data.length > 0) {
                finalChartData[key] = {
                    labels: groups[key].labels,
                    datasets: [{
                        label: ' Saldo',
                        data: groups[key].data,
                        backgroundColor: [
                            'rgba(255, 99, 132, 0.7)', 'rgba(54, 162, 235, 0.7)', 'rgba(255, 206, 86, 0.7)',
                            'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)', 'rgba(255, 159, 64, 0.7)',
                            'rgba(200, 100, 50, 0.7)', 'rgba(100, 200, 50, 0.7)', 'rgba(50, 100, 200, 0.7)'
                        ],
                        borderColor: [
                            'rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)',
                            'rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)', 'rgba(255, 159, 64, 1)',
                            'rgba(200, 100, 50, 1)', 'rgba(100, 200, 50, 1)', 'rgba(50, 100, 200, 1)'
                        ],
                        borderWidth: 1,
                    }],
                };
            }
        }
        setAssetChartData(finalChartData);
    };

    const processExpenseRevenueChartData = (data) => {
        const groups = ['modenacinema', 'giorgio', 'francesca', 'famiglia'];
        const finalChartData = {};

        groups.forEach(groupKey => {
            const groupData = data[groupKey];
            if (!groupData) return;

            // Process Expenses
            if (groupData.expense_details && groupData.expense_details.length > 0) {
                const labels = groupData.expense_details.map(d => d.name.replace(`${groupKey.toUpperCase().charAt(0)} - `, ''));
                const amounts = groupData.expense_details.map(d => Math.abs(d.amount)); // Use Math.abs for expenses
                
                finalChartData[`${groupKey}Expenses`] = {
                    labels: labels,
                    datasets: [{
                        label: 'Spese',
                        data: amounts,
                        backgroundColor: 'rgba(255, 99, 132, 0.7)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1,
                    }],
                };
            }

            // Process Revenues
            if (groupData.revenue_details && groupData.revenue_details.length > 0) {
                const labels = groupData.revenue_details.map(d => d.name.replace(`${groupKey.toUpperCase().charAt(0)} - `, ''));
                const amounts = groupData.revenue_details.map(d => d.amount);
                
                finalChartData[`${groupKey}Revenues`] = {
                    labels: labels,
                    datasets: [{
                        label: 'Ricavi',
                        data: amounts,
                        backgroundColor: 'rgba(75, 192, 192, 0.7)',
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 1,
                    }],
                };
            }
        });
        setExpenseRevenueChartData(finalChartData);
    };


    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            setError('');
            try {
                const formattedStartDate = format(startDate, 'yyyy-MM-dd');
                const formattedEndDate = format(endDate, 'yyyy-MM-dd');
                const response = await getAccountingDashboard(formattedStartDate, formattedEndDate);
                setDashboardData(response.data);
                if (response.data.asset_accounts) {
                    setAssetAccounts(response.data.asset_accounts);
                    processAssetChartData(response.data.asset_accounts);
                }
                processExpenseRevenueChartData(response.data); // Process expense/revenue data for charts
            } catch (err) {
                let errorMsg = 'Errore durante il caricamento della dashboard contabile.';
                if (err.response?.data?.detail) {
                    const detail = err.response.data.detail;
                    if (typeof detail === 'string') {
                        errorMsg = detail;
                    } else {
                        errorMsg = `Errore dettagliato: ${JSON.stringify(detail)}`;
                    }
                }
                setError(errorMsg);
                toast.error(errorMsg);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [startDate, endDate]);

    const renderCategoryCard = (title, data) => (
        <Col xs={12} md={6} lg={4} key={title} className="mb-4">
            <Card className="text-center h-100 shadow-sm">
                <Card.Header as="h5" className="bg-primary text-white">{title}</Card.Header>
                <Card.Body className="d-flex flex-column justify-content-between">
                    <div className={isMobile ? 'mobile-card-stats' : ''}>
                        <Card.Text className="fs-5 mb-2">Entrate:</Card.Text>
                        <Card.Text className="fs-3 text-success fw-bold">
                            € {data ? data.revenue.toFixed(2) : '0.00'}
                        </Card.Text>
                    </div>
                    <hr />
                    <div className={isMobile ? 'mobile-card-stats' : ''}>
                        <Card.Text className="fs-5 mb-2">Uscite:</Card.Text>
                        <Card.Text className="fs-3 text-danger fw-bold">
                            € {data ? Math.abs(data.expense).toFixed(2) : '0.00'}
                        </Card.Text>
                    </div>
                    <hr />
                    <div className={isMobile ? 'mobile-card-stats' : ''}>
                        <Card.Text className="fs-5 mb-2">Saldo:</Card.Text>
                        <Card.Text className={`fs-2 fw-bold ${data && (data.revenue + data.expense) >= 0 ? 'text-primary' : 'text-danger'}`}>
                            € {data ? (data.revenue + data.expense).toFixed(2) : '0.00'}
                        </Card.Text>
                    </div>
                    <hr />
                    <div className={isMobile ? 'mobile-card-stats' : ''}>
                        <Card.Text className="fs-5 mb-2">Passività:</Card.Text>
                        <Card.Text className="fs-3 text-danger fw-bold">
                            € {data && data.liability ? data.liability.toFixed(2) : '0.00'}
                        </Card.Text>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );

    const chartOptions = {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            label += new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(context.parsed);
                        }
                        return label;
                    }
                }
            },
            legend: {
                position: isMobile ? 'bottom' : 'right',
                labels: {
                    usePointStyle: true,
                    boxWidth: isMobile ? 12 : 20,
                    padding: isMobile ? 10 : 20
                }
            }
        }
    };
    
    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: isMobile ? 'bottom' : 'top',
                labels: {
                    boxWidth: isMobile ? 12 : 20,
                    padding: isMobile ? 10 : 20
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    font: {
                        size: isMobile ? 10 : 12
                    }
                }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    font: {
                        size: isMobile ? 10 : 12
                    },
                    callback: function(value) {
                        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
                    }
                }
            }
        }
    };


    return (
        <Container fluid>
            <style>{`
                @media (max-width: 991px) {
                    .mobile-date-input {
                        font-size: 16px;
                        min-height: 44px;
                        padding: 12px;
                    }
                    .chart-container {
                        height: 250px !important;
                    }
                    .mobile-card-stats {
                        text-align: center;
                        margin-bottom: 1rem;
                    }
                }
            `}</style>
            <h1 className="text-center my-4">Dashboard Contabile</h1>
            
            <Row className="mb-4 justify-content-center">
                <Col xs={12} md={8} lg={6}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Form>
                                <Row className="align-items-center">
                                    <Col sm={5} className="mb-2 mb-sm-0">
                                        <Form.Label htmlFor="startDatePicker" className="visually-hidden">Data Inizio</Form.Label>
                                        {isMobile ? (
                                            <Form.Control
                                                type="date"
                                                value={format(startDate, 'yyyy-MM-dd')}
                                                onChange={(e) => setStartDate(new Date(e.target.value))}
                                                className="form-control mobile-date-input"
                                            />
                                        ) : (
                                            <DatePicker
                                                id="startDatePicker"
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                dateFormat="dd/MM/yyyy"
                                                className="form-control"
                                                locale={it}
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        )}
                                    </Col>
                                    <Col sm={1} className="text-center mb-2 mb-sm-0">
                                        al
                                    </Col>
                                    <Col sm={5}>
                                        <Form.Label htmlFor="endDatePicker" className="visually-hidden">Data Fine</Form.Label>
                                        {isMobile ? (
                                            <Form.Control
                                                type="date"
                                                value={format(endDate, 'yyyy-MM-dd')}
                                                onChange={(e) => setEndDate(new Date(e.target.value))}
                                                min={format(startDate, 'yyyy-MM-dd')}
                                                className="form-control mobile-date-input"
                                            />
                                        ) : (
                                            <DatePicker
                                                id="endDatePicker"
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={startDate}
                                                dateFormat="dd/MM/yyyy"
                                                className="form-control"
                                                locale={it}
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Caricamento...</span>
                    </Spinner>
                    <p className="mt-3">Caricamento dati dashboard...</p>
                </div>
            ) : error ? (
                <Alert variant="danger" className="text-center my-5">{error}</Alert>
            ) : (
                <>
                    {isMobile ? (
                        // Mobile: Single column layout
                        <>
                            <Row className="mt-4 justify-content-center">
                                {dashboardData && renderCategoryCard("ModenaCinema", dashboardData.modenacinema)}
                            </Row>
                            <Row className="mt-2 justify-content-center">
                                {dashboardData && renderCategoryCard("Giorgio", dashboardData.giorgio)}
                            </Row>
                            <Row className="mt-2 justify-content-center">
                                {dashboardData && renderCategoryCard("Francesca", dashboardData.francesca)}
                            </Row>
                            <Row className="mt-2 justify-content-center">
                                {dashboardData && renderCategoryCard("Famiglia", dashboardData.famiglia)}
                            </Row>
                        </>
                    ) : (
                        // Desktop: Multi-column layout
                        <>
                            <Row className="mt-4 justify-content-center">
                                {dashboardData && renderCategoryCard("ModenaCinema", dashboardData.modenacinema)}
                                {dashboardData && renderCategoryCard("Giorgio", dashboardData.giorgio)}
                                {dashboardData && renderCategoryCard("Francesca", dashboardData.francesca)}
                            </Row>
                            <Row className="mt-4 justify-content-center">
                                {dashboardData && renderCategoryCard("Famiglia", dashboardData.famiglia)}
                            </Row>
                        </>
                    )}

                    {/* New Charts Section */}
                    <h2 className="text-center my-4">Dettaglio Saldi Conti Attività</h2>
                    <Row className="mt-5 justify-content-center">
                        {assetChartData && Object.keys(assetChartData).map(key => (
                            <Col xs={12} md={6} lg={5} key={`asset-${key}`} className="mb-4">
                                <Card className="h-100 shadow-sm">
                                    <Card.Header as="h5" className="text-center">Conti Attività: {key.charAt(0).toUpperCase() + key.slice(1)}</Card.Header>
                                    <Card.Body>
                                        <div className={isMobile ? 'chart-container' : ''} style={{ height: isMobile ? '250px' : '300px' }}>
                                            <Pie data={assetChartData[key]} options={chartOptions} />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <h2 className="text-center my-4">Dettaglio Spese e Ricavi per Categoria</h2>
                    <Row className="mt-4 justify-content-center">
                        <Col xs={12}>
                            <Accordion defaultActiveKey="0">
                                {expenseRevenueChartData && Object.keys(dashboardData).filter(key => key !== 'asset_accounts').map((groupKey, index) => (
                                    <Accordion.Item eventKey={index.toString()} key={groupKey}>
                                        <Accordion.Header>{groupKey.charAt(0).toUpperCase() + groupKey.slice(1)}</Accordion.Header>
                                        <Accordion.Body>
                                            <Row>
                                                {expenseRevenueChartData[`${groupKey}Expenses`] && (
                                                    <Col xs={12} md={6} className="mb-4">
                                                        <Card className="h-100 shadow-sm">
                                                            <Card.Header as="h5" className="text-center">Spese: {groupKey.charAt(0).toUpperCase() + groupKey.slice(1)}</Card.Header>
                                                            <Card.Body>
                                                                <div className={isMobile ? 'chart-container' : ''} style={{ height: isMobile ? '250px' : '300px' }}>
                                                                    <Bar data={expenseRevenueChartData[`${groupKey}Expenses`]} options={barChartOptions} />
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                )}
                                                {expenseRevenueChartData[`${groupKey}Revenues`] && (
                                                    <Col xs={12} md={6} className="mb-4">
                                                        <Card className="h-100 shadow-sm">
                                                            <Card.Header as="h5" className="text-center">Ricavi: {groupKey.charAt(0).toUpperCase() + groupKey.slice(1)}</Card.Header>
                                                            <Card.Body>
                                                                <div className={isMobile ? 'chart-container' : ''} style={{ height: isMobile ? '250px' : '300px' }}>
                                                                    <Bar data={expenseRevenueChartData[`${groupKey}Revenues`]} options={barChartOptions} />
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                )}
                                                {!expenseRevenueChartData[`${groupKey}Expenses`] && !expenseRevenueChartData[`${groupKey}Revenues`] && (
                                                    <Col xs={12} className="text-center text-muted">Nessun dato di spesa o ricavo dettagliato per questa categoria nel periodo selezionato.</Col>
                                                )}
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Col>
                    </Row>


                    <Row className="mt-4 justify-content-center">
                        <Col md={8}>
                            <Card className="shadow-sm">
                                <Card.Header as="h5" className="bg-secondary text-white">Saldi Conti Attività</Card.Header>
                                <Card.Body>
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Conto</th>
                                                <th className="text-end">Saldo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assetAccounts.map(account => (
                                                <tr key={account.name}>
                                                    <td>{account.name}</td>
                                                    <td className="text-end">€ {account.balance.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
};

export default ContabilitaDashboard;